/**
 * Privacy Policy content (Issue #2212, extracted from PrivacyPage.tsx / #386)
 *
 * Single source for web PrivacyPage and the native Privacy screen.
 * Legal text — content changes here are policy changes; keep the
 * "Last Updated" stamp in sync with any wording edit.
 */

import type { ContentDoc } from '../../types/content';

export const PRIVACY_DOC: ContentDoc = {
  slug: 'privacy',
  title: ['Privacy Policy'],
  subtitle: 'Clear, honest answers about how we handle your data',
  lastUpdated: 'August 2026',
  lastUpdatedPosition: 'top',
  callout: {
    title: "TL;DR (Because We Know You Won't Read the Whole Thing)",
    blocks: [
      { type: 'p', inlines: ["We don't sell your data."] },
      { type: 'p', inlines: ["We don't spy on you."] },
      { type: 'p', inlines: ['We don\'t hand your info to mysterious "partners."'] },
      {
        type: 'p',
        inlines: ['We collect the minimum required to keep the app (and your crew) running smoothly.'],
      },
      {
        type: 'p',
        inlines: [
          'If you give us your number, whether to sign in or for notifications, it goes to our texting provider, because that\'s how a text reaches a phone. That\'s the whole list. ',
          { t: 'strong', v: 'Never sold. Never rented. Never handed to marketers.' },
          ' Text STOP to opt out anytime, START to opt back in.',
        ],
      },
      {
        type: 'box',
        tone: 'accent',
        title: 'Geographic Scope:',
        blocks: [
          {
            type: 'p',
            inlines: [
              { t: 'brand' },
              ' primarily operates in the United States, but anyone anywhere can use it. You can ',
              { t: 'link', v: 'export your data or delete your account', href: '/settings/account' },
              ' directly from Settings. No support ticket needed. For other privacy questions, ',
              { t: 'link', v: 'contact us', href: '/contact' },
              '.',
            ],
          },
        ],
      },
      {
        type: 'p',
        inlines: [
          'Your data is ',
          {
            t: 'strong',
            v: 'encrypted on the way to us, and encrypted while it sits with us.',
          },
        ],
      },
      {
        type: 'p',
        inlines: [
          'It is ',
          { t: 'strong', v: 'not' },
          " end-to-end encrypted. We can read what's on our servers if we have to. We'd rather tell you that than let you assume otherwise.",
        ],
      },
      {
        type: 'labeled',
        label: ['The only people who ever see your stuff are:'],
        blocks: [
          {
            type: 'list',
            style: 'numbered',
            items: [
              [{ t: 'strong', v: 'You', tone: 'inherit' }, ','],
              [{ t: 'strong', v: 'The clubs you choose to join', tone: 'inherit' }, ', and'],
              [
                { t: 'strong', v: 'The unfortunate engineers keeping this thing running', tone: 'inherit' },
                { t: 'break' },
                '(a very short list, and they are too busy perfecting their latte art to care.)',
              ],
            ],
          },
        ],
      },
      {
        type: 'labeled',
        label: ['If you use GPS check-ins:'],
        blocks: [
          {
            type: 'list',
            style: 'bullet',
            items: [
              [
                'Your coordinates are sent ',
                { t: 'strong', v: 'securely', tone: 'inherit' },
                ' to our server,',
              ],
              [
                'Validated ',
                { t: 'strong', v: 'in memory', tone: 'inherit' },
                ' against the event location,',
              ],
              [
                'We save only ',
                { t: 'strong', v: 'yes/no', tone: 'inherit' },
                ' to the database,',
              ],
              [
                'And your coordinates are ',
                { t: 'strong', v: 'immediately discarded', tone: 'inherit' },
                ' (never stored).',
              ],
            ],
          },
        ],
      },
      {
        type: 'p',
        inlines: [
          "That's it.",
          { t: 'break' },
          'No drama. No shady surprises. No "gotchas."',
        ],
      },
      {
        type: 'p',
        emphasis: true,
        inlines: [
          'Just a simple promise: ',
          { t: 'strong', v: "your data isn't our business model.", tone: 'inherit' },
        ],
      },
      { type: 'p', inlines: ['Carry on!'] },
    ],
  },
  groups: [
    [
      {
        showDivider: true,
        blocks: [
          {
            type: 'p',
            inlines: [
              'Your privacy matters. We built ORO move from day one with the belief that ',
              { t: 'strong', v: 'your data belongs to you, not us.' },
              ' This page explains what we collect, how we use it, and how we protect it.',
            ],
          },
        ],
      },
    ],
    [
      {
        title: ['1. Data Required to Provide the Service'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'Certain information is required for ORO move to function, including account details, club participation, and event-related data.',
            ],
          },
          {
            type: 'p',
            inlines: [
              "By using the Service, you acknowledge that processing this information is necessary to provide core features like authentication, event coordination, and club participation. Without it, we literally can't run the app.",
            ],
          },
        ],
      },
      {
        title: ['2. What We Collect'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: ['We only collect the information needed for ORO move to work:'],
          },
          {
            type: 'list',
            style: 'bullet',
            items: [
              ['Your email address (for login links)'],
              ['Your phone number (if you sign in by text, or opt into SMS notifications)'],
              [
                "Someone else's phone number, if a club member enters it to invite them. We hold it to send that one invite, whether or not they ever join",
              ],
              ['Your profile details (name, photo if you choose to upload one)'],
              ['Optional location data (city/state on your profile, which you control and can edit or delete anytime)'],
              ['Club membership and RSVP activity'],
              ['Messages you post in clubs (if your club uses chat)'],
              ['Basic telemetry that helps us keep the app stable and detect abuse'],
              [
                'Sign-in records: when you signed in, from what IP address and browser, and roughly what country it looked like. Kept 90 days, so we can spot someone trying to get into your account.',
              ],
            ],
          },
          {
            type: 'p',
            inlines: [
              'We do ',
              { t: 'strong', v: 'not' },
              ' track your location, sell behavioral data, or run ads.',
            ],
          },
          {
            type: 'box',
            tone: 'surface',
            title: 'About GPS Check-Ins:',
            blocks: [
              {
                type: 'p',
                inlines: [
                  'If you check into an event using GPS, your coordinates are sent securely to our server, validated in memory to verify you\'re at the right location, then immediately discarded. We save only a "yes/no" response to the database. Your actual coordinates are never stored.',
                ],
              },
            ],
          },
        ],
      },
      {
        title: ['3. What We Never Do'],
        headingLevel: 'h3',
        blocks: [
          { type: 'p', inlines: ['Some things are absolute:'] },
          {
            type: 'list',
            style: 'bullet',
            items: [
              [{ t: 'strong', v: 'We never sell or rent your personal information.' }],
              [{ t: 'strong', v: 'We never share your data with advertisers.' }],
              [{ t: 'strong', v: 'We never allow third parties to harvest your information for profiling.' }],
            ],
          },
          { type: 'p', inlines: ['Your data will never be a revenue stream. Period.'] },
        ],
      },
      {
        title: ['4. How Your Data Is Protected'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'Your information is encrypted ',
              { t: 'strong', v: 'in transit' },
              ' from the moment it leaves your device and ',
              { t: 'strong', v: 'in storage' },
              " once it reaches our infrastructure. When data returns to you, it's encrypted again until it hits your screen.",
            ],
          },
          { type: 'p', inlines: ['We use:'] },
          {
            type: 'list',
            style: 'bullet',
            items: [
              ['TLS on every connection, in both directions'],
              ['Encryption at rest, provided by our hosting platform'],
              ['Hardened server and database infrastructure'],
              ['Strict access controls'],
              ['Industry-standard hashing for authentication tokens'],
              ['Audit logging on sensitive actions'],
            ],
          },
          {
            type: 'p',
            inlines: [
              'We design systems so the safest assumption is ',
              { t: 'strong', v: '"only you should see your data."' },
            ],
          },
        ],
      },
    ],
    [
      {
        title: ['5. How We Use Your Data'],
        headingLevel: 'h3',
        blocks: [
          { type: 'p', inlines: ['We use your information only to:'] },
          {
            type: 'list',
            style: 'bullet',
            items: [
              ['Help you access your account'],
              ['Join and participate in clubs'],
              ['Create and RSVP to events'],
              ["Send notifications you've opted into"],
              ['Keep the platform secure and detect misuse'],
            ],
          },
          {
            type: 'p',
            inlines: [
              "We don't repurpose your data for anything outside of running ORO move.",
            ],
          },
        ],
      },
      {
        title: ['6. SMS Communications'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              "If you sign in with your phone number, we use it to send one-time sign-in codes. If you also opt into SMS notifications, we'll use it for event and club activity. And if a club member enters your number to invite you, we text you that invite, even if you've never used ORO move before. That is the whole list: ",
              { t: 'strong', v: 'sign-in, the notifications you chose, and invites' },
              '. Full detail on the ',
              { t: 'link', v: 'SMS Help page', href: '/help/sms' },
              '.',
            ],
          },
          {
            type: 'labeled',
            label: ['What we promise:'],
            blocks: [
              {
                type: 'list',
                style: 'bullet',
                items: [
                  [
                    { t: 'strong', v: 'No third-party sharing' },
                    ' — your phone number will never be sold, rented, or shared with third parties for marketing purposes',
                  ],
                  [{ t: 'strong', v: 'No spam' }, " — only messages you've asked for"],
                  [
                    { t: 'strong', v: 'Easy opt-out' },
                    ' — text ',
                    { t: 'strong', v: 'STOP', tone: 'inherit' },
                    ' at any time to stop receiving messages, or ',
                    { t: 'strong', v: 'START', tone: 'inherit' },
                    ' to opt back in',
                  ],
                ],
              },
            ],
          },
          {
            type: 'p',
            inlines: [
              'Standard message and data rates may apply depending on your carrier. For more information, visit our ',
              { t: 'link', v: 'SMS Help page', href: '/help/sms' },
              '.',
            ],
          },
        ],
      },
      {
        title: ['7. When We Share Data'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'We share the minimum needed with the services that make the app work:',
            ],
          },
          {
            type: 'list',
            style: 'bullet',
            items: [
              [
                { t: 'strong', v: 'Hosting and storage' },
                ' — everything lives with our cloud infrastructure provider',
              ],
              [
                { t: 'strong', v: 'Email' },
                ' — sign-in links, reminders, notifications',
              ],
              [
                { t: 'strong', v: 'Text messages' },
                ' — sign-in codes, the alerts you opted into, and club invitations, including to people who have never used ORO',
              ],
              [
                { t: 'strong', v: 'Push notifications' },
                ' — routed through Apple or Google to reach your phone, including the preview line of a message',
              ],
              [
                { t: 'strong', v: 'Error monitoring' },
                ' — what broke and where',
              ],
              [
                { t: 'strong', v: 'Maps and address lookup' },
                ' — when you search for a place, or tap "use my current location," that lookup leaves our servers',
              ],
              [
                { t: 'strong', v: 'Feedback triage' },
                ' — if you send feedback, an AI service summarizes it and we file it as a work ticket',
              ],
            ],
          },
          {
            type: 'p',
            inlines: [
              'Each one gets only what it needs to do its job. ',
              { t: 'strong', v: 'None of them are advertisers or data brokers.' },
            ],
          },
          {
            type: 'p',
            inlines: [
              'We do ',
              { t: 'strong', v: 'not' },
              ' sell or broker your data. Ever.',
            ],
          },
          {
            type: 'p',
            inlines: [
              { t: 'strong', v: "Want to know exactly who they are?" },
              " Ask us and we'll tell you.",
            ],
          },
        ],
      },
      {
        title: ['8. Cookies & Session Management'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'ORO move uses cookies and session tokens to keep you logged in and recognize your device.',
            ],
          },
          {
            type: 'labeled',
            label: ['What we use:'],
            blocks: [
              {
                type: 'list',
                style: 'bullet',
                items: [
                  [
                    { t: 'strong', v: 'Session tokens', tone: 'inherit' },
                    ' (HttpOnly, Secure cookies) — stored safely server-side, never accessible to scripts',
                  ],
                  [
                    { t: 'strong', v: 'Error monitoring', tone: 'inherit' },
                    " — when something breaks, we get a report so we can fix it: the error, and the page it happened on. We've turned off the IP-address collection the tool offers, though a report can still carry an ID that points back to your account. We don't run usage analytics or behavioral tracking.",
                  ],
                  [
                    { t: 'strong', v: 'No third-party cookies', tone: 'inherit' },
                    " — we don't let advertisers or trackers plant cookies on you",
                  ],
                ],
              },
            ],
          },
          {
            type: 'p',
            inlines: [
              "These cookies expire automatically. You can clear them anytime. You'll just need to log back in next time.",
            ],
          },
        ],
      },
      {
        title: ['9. Club Visibility'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: ['ORO move lets clubs control their discoverability:'],
          },
          {
            type: 'list',
            style: 'bullet',
            items: [
              [
                { t: 'strong', v: 'Public' },
                ' — Discoverable, but joining requires approval from club leadership',
              ],
              [
                { t: 'strong', v: 'Invite-only' },
                ' — Discoverable, but requires an invitation to join (default for new clubs)',
              ],
              [
                { t: 'strong', v: 'Private' },
                ' — Hidden from discovery, invitation only',
              ],
            ],
          },
          {
            type: 'p',
            inlines: [
              "All clubs are members-only spaces. Club owners and leaders control who can join, and you can leave any club at any time, unless you're the last leader standing. Then you'll need to promote someone else first, so the club isn't left with nobody running it.",
            ],
          },
        ],
      },
    ],
    [
      {
        title: ['10. Data You Delete'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'When you delete something yourself, whether a profile photo, a cover image, or an event you created, it comes out of our active systems. Backups may retain fragments for a short period, but they age out automatically. (Closing your whole account is different for events: they transfer to the club owner rather than disappearing, so the people who RSVP\'d keep theirs.)',
            ],
          },
          {
            type: 'p',
            inlines: [
              { t: 'strong', v: 'Deleting a chat message is different.' },
              ' It disappears from the room, but we keep the text, and any photo attached to it, so we can investigate a report about a conversation after the fact. We would rather tell you that than let "delete" mean two different things on the same screen. Our ',
              { t: 'link', v: 'Terms', href: '/terms' },
              ' say the same.',
            ],
          },
          {
            type: 'labeled',
            label: ['Account Deletion:'],
            blocks: [
              {
                type: 'p',
                inlines: [
                  'You can permanently delete your account from ',
                  { t: 'link', v: 'Settings → Account & Privacy', href: '/settings/account' },
                  '. We send a confirmation before anything is deleted. If you have an email on file, we email you a link; if you signed up with just a phone number, we text you a six-digit code to enter in the app. Once you confirm, your profile, RSVPs, check-ins, and personal data are removed immediately. Your chat messages stay in club history but are anonymized as ',
                  { t: 'mono', v: '[Deleted User]' },
                  ', and events you created transfer to the club owner.',
                ],
              },
              {
                type: 'p',
                inlines: [
                  { t: 'strong', v: 'The exception is opt-out texts.', tone: 'inherit' },
                  ' If you have ever texted our number ',
                  { t: 'strong', v: 'STOP', tone: 'inherit' },
                  ', ',
                  { t: 'strong', v: 'START', tone: 'inherit' },
                  ', or ',
                  { t: 'strong', v: 'HELP', tone: 'inherit' },
                  ', we keep that keyword, the number it came from, and when it arrived, indefinitely. Not because we want your number, but because "prove they stopped texting you" is something we may one day have to do, and "we deleted the evidence" is not an answer. Any other text you sent our number has its content and your number cleared when you delete your account, and the contents of the notifications we sent you are blanked too. Only the opt-out evidence outlives your account.',
                ],
              },
              {
                type: 'p',
                inlines: [
                  { t: 'strong', v: 'What we keep:', tone: 'inherit' },
                  ' We keep a record that the deletion happened: an account ID, a timestamp, and the IP address and browser it came from. We hold it for two years, so we can defend against someone falsely claiming they deleted an account they did not. It is not a copy of your data, and nothing in it is used for anything else.',
                ],
              },
            ],
          },
          {
            type: 'labeled',
            label: ['Privacy Rights: exercise them yourself'],
            blocks: [
              {
                type: 'list',
                style: 'bullet',
                items: [
                  [
                    { t: 'link', v: 'Data export', href: '/settings/account', bold: true },
                    ' — download a JSON file of your data: profile, clubs, events, RSVPs, check-ins, messages, invites, and your notification settings (GDPR Article 15)',
                  ],
                  [
                    { t: 'link', v: 'Account deletion', href: '/settings/account', bold: true },
                    ' — we erase your personal data and close the account. Your messages stay in club history as [Deleted User], so conversations still make sense for everyone else. A record that the deletion happened is kept for two years, and anything you sent us directly, such as survey feedback or a request to join a club, stays too, still linked to your old account ID (GDPR Article 17)',
                  ],
                  [
                    { t: 'link', v: 'Data corrections', href: '/settings/profile', bold: true },
                    ' — edit your profile fields directly',
                  ],
                ],
              },
              {
                type: 'p',
                inlines: [
                  "If you'd prefer a manual request or have questions about any of the above, ",
                  { t: 'link', v: 'contact us', href: '/contact' },
                  " and we'll respond within 30 days.",
                ],
              },
            ],
          },
        ],
      },
      {
        title: ["11. Children's Privacy"],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'ORO move is not designed for children under 13. If we learn an underage user created an account, we remove it.',
            ],
          },
        ],
      },
      {
        title: ['12. Changes to This Policy'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              "If we ever make a meaningful change to how we handle privacy, we'll tell you clearly in the app, not buried in fine print.",
            ],
          },
        ],
      },
      {
        title: ['13. Contact'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'ORO move is run by ',
              { t: 'strong', v: 'ORO, LLC' },
              ', in the United States. That is the company responsible for your data, the "we" throughout this policy.',
            ],
          },
          {
            type: 'p',
            inlines: ['Questions or concerns about privacy, data, or these policies?'],
          },
          {
            type: 'p',
            inlines: [
              { t: 'link', v: 'Contact us through our support form', href: '/contact' },
              ', or email ',
              { t: 'link', v: 'support@oro.fit', href: 'mailto:support@oro.fit' },
              '.',
            ],
          },
          {
            type: 'p',
            inlines: [
              "We take privacy seriously, and we'll respond within 30 days to all requests.",
            ],
          },
          {
            type: 'p',
            inlines: [
              { t: 'strong', v: 'Asking costs you nothing.' },
              " Requesting your data, or asking us to delete it, will never change your price, your features, or how we treat your account. There is no premium tier for privacy.",
            ],
          },
          {
            type: 'p',
            inlines: [
              { t: 'strong', v: "And you can go over our heads." },
              " If you're in the UK or the EU and you think we've handled your data badly, you can complain to your national data protection authority. You don't need our permission and you don't have to come to us first, though we'd rather you did, because we can actually fix things.",
            ],
          },
        ],
      },
    ],
  ],
};
